import { ref } from 'vue'
import { client } from './api'
import { clearControlCenterToken } from '@/composables/useControlCenterToken'

// No localStorage/sessionStorage here on purpose (Teil 5.1): the session
// lives in an httpOnly cookie the frontend can't and shouldn't read. Identity
// is instead fetched on demand via GET /portal/me and cached in this ref for
// the lifetime of the page.
export const currentUser = ref(null)
let meLoaded = false

export function getUser() {
  return currentUser.value
}

export function getRole() {
  return currentUser.value?.role || null
}

export function hasRole(...roles) {
  return roles.includes(getRole())
}

// is_external is a DB-enforced invariant of role === 'reader' (CHECK
// constraint users_reader_is_external_check, see users.py) - never diverges,
// so this is exact, not a guess.
export function isReader() {
  return getRole() === 'reader'
}

// Best-effort synchronous check for render decisions before ensureMe() has
// resolved (e.g. an initial paint). The router guard's ensureMe() call is
// the authoritative check - this just avoids a flash of the wrong UI.
export function isLoggedIn() {
  return !!currentUser.value
}

// Single source of truth for identity, for BOTH the internal app and the
// reader portal. GET /portal/me works for any authenticated session sitting
// on requires_auth(allow_external=True) — that flag only lifts the
// deny-by-default for external users, it does not exclude internal ones, so
// this one endpoint serves as ensureMe() for the whole app instead of two
// diverging internal/external "who am I" implementations.
export async function ensureMe({ force = false } = {}) {
  if (currentUser.value && meLoaded && !force) return currentUser.value
  try {
    const resp = await client.get('/portal/me')
    currentUser.value = {
      ...resp.data,
      page_keys: resp.data.pages,
      is_external: resp.data.role === 'reader',
    }
    meLoaded = true
    return currentUser.value
  } catch (e) {
    currentUser.value = null
    meLoaded = false
    // Never re-throw: this runs inside the router's beforeEach guard (see
    // router/index.js), and an uncaught rejection there doesn't just fail
    // one navigation — it crashes the entire router install ("Unexpected
    // error when starting the router"), leaving a blank app with no way to
    // even reach the login page. A 401 (no/invalid session) and a network-
    // level failure (backend unreachable, CORS misconfigured for this
    // origin — no e.response at all in that case) both mean the same thing
    // from the guard's point of view: we can't confirm this user is logged
    // in, so treat it as logged out and let the guard redirect to login.
    // The real cause still needs to be visible somewhere, so log it instead
    // of swallowing it silently.
    if (e.response?.status !== 401) {
      console.error('[auth] ensureMe() failed - treating as logged out', e)
    }
    return null
  }
}

export function clearMe() {
  currentUser.value = null
  meLoaded = false
}

// ── Internal login/logout (unchanged endpoints, now cookie-based) ─────────

export async function login(email, password) {
  const resp = await client.post('/auth/login', { email, password })
  // Variante-A-Auftrag: ein vorheriger Nutzer im selben Tab (bzw. dessen
  // Control-Center-Token) darf nach diesem Login nicht weiter gueltig
  // bleiben - siehe useControlCenterToken.js.
  clearControlCenterToken()
  const basicUser = { ...resp.data.user, page_keys: [], is_external: false }
  currentUser.value = basicUser
  // Fetch pages/language etc. so the sidebar renders correctly right away.
  // ensureMe() never throws (see its own comment) - it resolves to null on
  // ANY failure, including a transient blip on this immediate follow-up
  // call. That must not undo a login that just genuinely succeeded, so fall
  // back to the minimal user info from the login response itself instead of
  // accepting ensureMe()'s null.
  const full = await ensureMe({ force: true })
  if (!full) currentUser.value = basicUser
  return currentUser.value
}

export async function logout() {
  try {
    const path = currentUser.value?.is_external ? '/portal/auth/logout' : '/auth/logout'
    await client.post(path)
  } catch {
    // best-effort — clear local state regardless
  }
  clearControlCenterToken()
  clearMe()
}
